import React, { createContext, useContext, useState, useCallback, useMemo } from 'react';
import { useAuth } from './AuthContext';
import Logger from '../utils/Logger';

/**
 * ImpersonationContext
 *
 * Lets superadmins "view as" another crew member to see
 * how the app looks with their permission set.
 *
 * Important: This is frontend-only. Backend queries still use the
 * real auth.uid — so the data returned is the admin's full dataset,
 * but permissions / visibility are applied as if the impersonated
 * user was logged in.
 */

// Hard-coded superadmin UIDs — only these users can impersonate.
// Add your Firebase uid here.
const SUPERADMIN_UIDS = [
    'dqfmCGxFh0WjyXvJzx8AvCLUF5f2', // Eirik
];

const ImpersonationContext = createContext({
    isImpersonating: false,
    impersonatedUser: null,
    impersonatedCrew: null,
    artistId: null,
    artistName: null,
    isSuperAdmin: false,
    startImpersonation: () => {},
    stopImpersonation: () => {},
    getEffectiveUid: () => null,
});

export const useImpersonation = () => {
    const context = useContext(ImpersonationContext);
    if (!context) {
        throw new Error('useImpersonation must be used within an ImpersonationProvider');
    }
    return context;
};

export const ImpersonationProvider = ({ children }) => {
    const { user } = useAuth();

    // State
    const [impersonatedUser, setImpersonatedUser] = useState(null);     // { uid, firstName, lastName, email }
    const [impersonatedCrew, setImpersonatedCrew] = useState(null);     // The full crew member record with permissions
    const [artistId, setArtistId] = useState(null);
    const [artistName, setArtistName] = useState(null);

    const isSuperAdmin = useMemo(
        () => !!user && SUPERADMIN_UIDS.includes(user.uid),
        [user]
    );

    const isImpersonating = !!impersonatedUser;

    /**
     * Start impersonating a crew member.
     * @param {string} targetArtistId   The artist this crew member belongs to.
     * @param {string} targetArtistName Display name of the artist.
     * @param {object} crewMember       The full crew member record from artist.crewMembers_on_artist.
     *                                  Must include .user.id, .user.firstName, .user.lastName, and permission fields.
     */
    const startImpersonation = useCallback((targetArtistId, targetArtistName, crewMember) => {
        if (!isSuperAdmin) {
            Logger.warn('ImpersonationContext', 'Non-superadmin tried to impersonate');
            return;
        }
        if (!crewMember?.user?.id) {
            Logger.warn('ImpersonationContext', 'Invalid crew member for impersonation', crewMember);
            return;
        }

        Logger.info('ImpersonationContext', 'Starting impersonation', {
            artistId: targetArtistId,
            userId: crewMember.user.id,
            userName: `${crewMember.user.firstName} ${crewMember.user.lastName}`,
        });

        setArtistId(targetArtistId);
        setArtistName(targetArtistName);
        setImpersonatedUser({
            uid: crewMember.user.id,
            firstName: crewMember.user.firstName,
            lastName: crewMember.user.lastName,
            email: crewMember.user.email,
        });
        setImpersonatedCrew(crewMember);
    }, [isSuperAdmin]);

    const stopImpersonation = useCallback(() => {
        Logger.info('ImpersonationContext', 'Stopping impersonation');
        setImpersonatedUser(null);
        setImpersonatedCrew(null);
        setArtistId(null);
        setArtistName(null);
    }, []);

    /**
     * Returns the effective user ID for permission checks.
     * If impersonating → returns the impersonated user's uid.
     * Otherwise → returns the real user's uid.
     */
    const getEffectiveUid = useCallback(() => {
        if (isImpersonating && impersonatedUser?.uid) {
            return impersonatedUser.uid;
        }
        return user?.uid || null;
    }, [isImpersonating, impersonatedUser, user]);

    const value = useMemo(() => ({
        isImpersonating,
        impersonatedUser,
        impersonatedCrew,
        artistId,
        artistName,
        isSuperAdmin,
        startImpersonation,
        stopImpersonation,
        getEffectiveUid,
    }), [
        isImpersonating,
        impersonatedUser,
        impersonatedCrew,
        artistId,
        artistName,
        isSuperAdmin,
        startImpersonation,
        stopImpersonation,
        getEffectiveUid,
    ]);

    return (
        <ImpersonationContext.Provider value={value}>
            {children}
        </ImpersonationContext.Provider>
    );
};

export default ImpersonationContext;
